import { shallowEqual, useSelector } from 'react-redux';
import cx from 'classnames';
import Button, { Align, Color, Size } from 'lib/Button';
import { Line } from 'components/common/types/Deal.types';
import Tooltip, { TooltipDirection, TooltipSize } from 'lib/Tooltip';
import LineSelect from 'components/pages/Planner/PlannerHeader/LineSelect';
import { CampaignPlannerMode } from 'store/dealManagement/types';
import { getIsAnyLineFetchingAvailability, getIsLineBeingSaved } from 'store/dealManagement/selectors';
import { getIsDirectSalesCampaignType } from 'store/dealManagement/selectors/getCampaignType';
import { DirectSalesLockedLineStatusIcon, getIsDirectSalesLineLocked } from 'components/common/LockStateStatusIcon';
import { DirectSalesNavigationLineData } from 'components/common/types/DirectSalesHeader';
import { getBackupLines } from 'store/dealManagement/selectors/getBackupLines';
import { getDealId } from 'store/dealManagement/selectors/getDealId';
import { getCurrentLine } from 'store/dealManagement/selectors/getCurrentLine';
import { getCampaignPlannerMode } from 'store/dealManagement/selectors/getCampaignPlannerMode';
import { getIsEditingDisabled } from 'store/dealManagement/selectors/getIsEditingDisabled';
import { DealLinesProps } from './DealLines.types';
import AddNewLineButton from '../AddNewLineButton/AddNewLineButton';

export const DealLines: React.FC<DealLinesProps> = ({
  displayedLines,
  displayDealLinesDropdown,
  onLineSelect,
  onAddNewLineClick,
  isAddNewLineDisabled,
  isSelectLineDisabled,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const lines = useSelector(getBackupLines, shallowEqual);
  const dealId = useSelector(getDealId);
  const currentLine = useSelector(getCurrentLine);
  const isEditingDisabled = useSelector(getIsEditingDisabled);
  const isReviewMode = useSelector(getCampaignPlannerMode) === CampaignPlannerMode.REVIEW;
  const isDirectSalesCampaignType = useSelector(getIsDirectSalesCampaignType);
  const isFetchingAvailability = useSelector(getIsAnyLineFetchingAvailability);
  const isLineBeingSaved = useSelector(getIsLineBeingSaved);

  const noDirectSalesCampaign = isDirectSalesCampaignType && !dealId;

  const isLineActive = (index: number, line: Line | DirectSalesNavigationLineData): boolean => {
    const isSingleLine = index === 0 && displayedLines.length === 1;

    if (isDirectSalesCampaignType) return isSingleLine || currentLine.lineId === line.lineId;
    return currentLine.lineId === line.lineId;
  };

  return (
    <>
      {displayedLines?.map((line, index) => {
        const isDisplayedLineActive = isLineActive(index, line);
        const isLineButtonDisabled =
          noDirectSalesCampaign ||
          ((isSelectLineDisabled || isEditingDisabled || isFetchingAvailability || isLineBeingSaved) &&
            !isDisplayedLineActive);
        const isLineLocked = getIsDirectSalesLineLocked(line);

        const getButtonColor = (): Color => {
          if (isLineLocked && isDisplayedLineActive) return Color.ORANGE;
          return isDisplayedLineActive ? Color.TERTIARY : Color.BLACK_AND_WHITE;
        };

        return (
          <Tooltip
            tooltip={line.name}
            direction={TooltipDirection.BOTTOM}
            contentSize={TooltipSize.MAX}
            key={line.lineId ?? `${line.name}-${line.id}`}
            wrapperClassName="flex items-center"
          >
            <Button
              onClick={() => (isDisplayedLineActive ? undefined : onLineSelect?.(line))}
              color={getButtonColor()}
              dataTestId={`planner-header-button-${line.name?.replace(/ /g, '')}`}
              isDisabled={isLineButtonDisabled}
              size={Size.SMALL}
              label={!line.name ? 'Deal line 1' : line.name}
              align={Align.START}
              classNames={cx('border flex flex-row-reverse items-center max-w-[12ch] overflow-hidden leading-tight', {
                'border-primary-600': isDisplayedLineActive && !isLineLocked,
              })}
            >
              {isLineLocked && <DirectSalesLockedLineStatusIcon />}
            </Button>
          </Tooltip>
        );
      })}
      {displayDealLinesDropdown ? (
        <LineSelect
          lines={lines}
          onSelectOne={onLineSelect}
          onAddNewLineClick={onAddNewLineClick}
          selectedLineId={currentLine.lineId}
          isDisabled={isEditingDisabled}
          isAddNewLineDisabled={isAddNewLineDisabled}
          isSelectLineDisabled={isSelectLineDisabled}
        />
      ) : (
        !isReviewMode && <AddNewLineButton isDisabled={isAddNewLineDisabled} onClick={onAddNewLineClick} />
      )}
    </>
  );
};
